export default `# React Application Structure

Below is an example of a barebone React application:

\`\`\`codeSandbox
jz866zwxy5
\`\`\`

In the top left corner, you can see a *hamburger* icon. When you press it, an additional panel will slide in.
There are already 3 files in the project:
- \`index.css\` (empty)
- \`index.html\`
- \`index.js\`

Below the files panel, there is a dependencies panel listing: \`react\` and \`react-dom\`.
The dependencies are external packages that you plug into the project.

The \`react\` package contains all React functionalities. The \`react-dom\` package is responsible for rendering elements on the DOM.
And you need both to build a React app.

## Rendering

First, let's inspect the \`index.js\` file.

In the previous chapter, when creating a class component, you had to extend the \`React.Component\` class.

For brevity of code, \`import\` statements were hidden. But from now on, you will have to add them explicitly.

In this project, you have to import both – \`React\` and \`ReactDOM\`:

\`\`\`plainJavascript
import React from 'react';
import ReactDOM from 'react-dom';
\`\`\`

Below import statements, you can see the \`App\` component.

Do you remember the \`render\` function from the previous chapters? It was responsible for displaying the \`Title\` component.

In every React application, there is one component that wraps the whole application. It's often called the \`App\`. And you have to render it first,
using \`ReactDOM.render\` method.

Let's now inspect the \`index.html\` file. It contains a lonely \`div\` element with an \`id="root"\`. It is used by the \`ReactDOM.render\` method.

The \`ReactDOM.render\` method takes two arguments:
- A component to render,
- An HTML element inside which the component will be rendered

In our case, we want to render an \`App\` component inside of the \`div\` with \`id="root"\`:

\`\`\`plainJavascript
ReactDOM.render(<App />, document.getElementById("root"));
\`\`\`


## Exporting and Importing Components

\`Import\` and \`export\` statements are used to make the code from one file accessible in another file.

Imagine that you have two components from the previous chapter:
- \`MovieListItem\` in the *MovieListItem.js* file
- \`MovieList\` in the *MovieList.js* file

If you want to use the \`MovieListItem\` in the \`MovieList\` file, first, you have to:
- \`export\` the \`MovieListItem\` from the *MovieListItem.js* file
- \`import\` the \`MovieListItem\` into the *MovieList.js* file

There are two ways of exporting a component:
- named export
- default export

## Named Export

The \`named export\` exports a component with a predefined name. It means that if you want to use it in another component, you have to import it using this exact name.
Named exports are handy when you want to export more than one component from a single file.

Let's see what it looks like. Here is our *MovieListItem.js* file:

\`\`\`plainJavascript
import React from "react";

export const MovieListItem = ({ movieTitle }) => (
	<li>{movieTitle}</li>
)
\`\`\`

Above, the \`MovieListItem\` component in declared in a standard way. But the declaration is preceded with the \`export\` keyword.

Now, if you want to use the \`MovieListItem\` in the *MovieList.js* file you have to \`import\` it first:
\`\`\`plainJavascript
import { MovieListItem } from "./MovieListItem";
\`\`\`

The \`import\` keyword is followed by the name of the component wrapped in curly braces and its location.

You can declare and export many components from a single file using named export. And then import them elsewhere by wrapping their names into curly braces.

## Default Export

The second type of export is a \`default export\`. It is used when you want to export only one component from a file.

\`\`\`plainJavascript
const MovieListItem = ({ movieTitle }) => (
	<li>{movieTitle}</li>
);

export default MovieListItem;
\`\`\`

or

\`\`\`plainJavascript
export default ({ movieTitle }) => (
	<li>{movieTitle}</li>
);
\`\`\`

Later, when you want to import the \`MovieListItem\` into another file you have to omit the curly braces:
\`\`\`plainJavascript
import MovieListItem from "./MovieListItem";
\`\`\`

Because the default export exported the \`MovieListItem\` without a predefined name, you can import the \`MovieListItem\` and assign it another name:
\`\`\`plainJavascript
import Item from "./MovieListItem";
\`\`\`
`;



// WEBPACK FOOTER //
// ./src/book/chapters/react-structure.js